import {
  Box,
  CheckSquare,
  Download,
  Edit2,
  FileText,
  List,
  Settings,
  Sliders,
  Upload,
} from "lucide-react";
import { useCallback, useEffect, useReducer, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { fetchWithAuth } from "../../../api-client/api_client";
import {
  AsyncTaskExecution,
  AsyncTaskExecutionPreview,
  BlueprintOperationType,
  BlueprintTestPayload,
  BlueprintVersionPublishState,
  JSONObjectSchema,
} from "../../../models/Blueprints";
import { CommonModelInfo, LinkedAccount } from "../../../models/Entities";
import {
  BLUEPRINT_EDITOR_README_TAB_PATH,
  getBlueprintEditorPath,
  getBlueprintEditorReturnSchemaTabPath,
  getBlueprintEditorValidationTabPath,
  getBlueprintReadmeTabPath,
} from "../../../router/RouterUtils";
import { getReturnSchemaForScraper } from "../../scraper/utils/ScraperUtils";
import IntegrationEditorLeftPanel, {
  IntegrationEditorLeftPanelVerticalTab,
} from "../../shared/integration-editor-base/IntegrationEditorLeftPanel";
import { showErrorToast } from "../../shared/Toasts";
import useBlueprintContext from "../context/useBlueprintContext";
import {
  AbstractConditionAction,
  AbstractConditionReducerState,
  abstractConditionsForSelectiveSyncReducer,
} from "../reducers/AbstractConditionsForSelectiveSyncReducer";
import {
  initializeInputParams,
  initializeInputParamsForProxy,
  isBlueprintWriteOperation,
} from "../utils/BlueprintEditorUtils";
import BlueprintEditorLeftPanelConnectorSubtab from "./BlueprintEditorLeftPanelConnectorSubtab";
import BlueprintEditorLeftPanelConsoleSubtab from "./BlueprintEditorLeftPanelConsoleSubtab";
import BlueprintEditorLeftPanelOverviewSubtab from "./BlueprintEditorLeftPanelOverviewSubtab";
import BlueprintEditorLeftPanelSchemasSubtab from "./BlueprintEditorLeftPanelSchemasSubtab";
import useFetchTestLinkedAccounts from "./hooks/useFetchTestLinkedAccounts";

const OPERATION_TYPES_WITH_FIELD_VALIDATION = [
  BlueprintOperationType.CREATE,
  BlueprintOperationType.BULK_CREATE,
  BlueprintOperationType.UPSERT,
  BlueprintOperationType.EDIT,
  BlueprintOperationType.META,
];

const QUICKBOOKS_DESKTOP_INTEGRATION_ID = "4f340c8e-10d1-4989-8c36-579c6e0f0b3c";

export function updateMaxLoopIterationPerStep(
  maxLoopIterationPerStep: { [stepID: string]: number },
  stepID: string,
  value: number | undefined,
  setMaxLoopIterationPerStep: (x: { [stepID: string]: number }) => void
): void {
  if (value && value !== 0) {
    setMaxLoopIterationPerStep({ ...maxLoopIterationPerStep, [stepID]: value });
  } else {
    const updatedMaxLoopIterationPerStep = Object.fromEntries(
      Object.entries(maxLoopIterationPerStep).filter(([key]) => key !== stepID)
    );
    setMaxLoopIterationPerStep(updatedMaxLoopIterationPerStep);
  }
}

interface BlueprintEditorLeftPanelProps {
  isCustomerLinkedAccountSelected: boolean;
  setIsCustomerLinkedAccountSelected: (x: boolean) => void;
  width: number;
}

const BlueprintEditorLeftPanel = ({
  isCustomerLinkedAccountSelected,
  setIsCustomerLinkedAccountSelected,
  width,
}: BlueprintEditorLeftPanelProps) => {
  const {
    blueprint,
    doesBlueprintHaveUnsavedChanges,
    codeBlockBranch,
    setCodeBlockBranch,
  } = useBlueprintContext();
  const {
    parameter_schema,
    human_name,
    name,
    operation_type,
    integration,
    version,
    qbxml_query_request_format,
    category,
  } = blueprint;
  const [selectedTestLinkedAccount, setSelectedTestLinkedAccount] = useState<undefined | string>();
  const [selectedTestCommonModel, setSelectedTestCommonModel] = useState<undefined | string>();
  const [isTestingBlueprint, setIsTestingBlueprint] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowingModelsModal, setIsShowingModelsModal] = useState<boolean>(true);
  const [testLinkedAccounts, setTestLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [isLoadingTestLinkedAccounts, setIsLoadingTestLinkedAccounts] = useState<boolean>(false);
  const [testCommonModels, setTestCommonModels] = useState<undefined | CommonModelInfo[]>();
  const [disableFilterByDate, setDisableFilterByDate] = useState<boolean>(false);
  const [disableSyncCursor, setDisableSyncCursor] = useState<boolean>(false);
  const [shouldUseLastScraperResults, setShouldUseLastScraperResults] = useState<boolean>(
    blueprint.scraper !== undefined
  );
  const [shouldFilterDisabledFields, setShouldFilterDisabledFields] = useState<boolean>(false);
  const [shouldRunAsAsyncTask, setShouldRunAsAsyncTask] = useState<boolean>(false);
  const [shouldGenerateMappingTest, setShouldGenerateMappingTest] = useState<boolean>(false);
  const [asyncTaskExecutionID, setAsyncTaskExecutionID] = useState<string | undefined>();
  const [asyncTaskExecutionResult, setAsyncTaskExecutionResult] = useState<
    AsyncTaskExecution | undefined
  >();
  const [maxLoopIterations, setMaxLoopIterations] = useState<number>();
  const [maxLoopIterationsPerStep, setMaxLoopIterationsPerStep] = useState<{
    [stepID: string]: number;
  }>({});
  const [maxPageIterations, setMaxPageIterations] = useState<number>();
  const [frozenTimeValue, setFrozenTimeValue] = useState<string>();
  const [overrideLastRunAtValue, setOverrideLastRunAtValue] = useState<string>();
  const [shouldIncludeNestedParams, setShouldIncludeNestedParams] = useState<boolean>(false);
  const [globalVarsAsString, setGlobalVarsAsString] = useState<string>();
  const [initializedSchemaForDeletePayload, setInitializedSchemaForDeletePayload] = useState<
    string
  >("");
  const [recentTestRuns, setRecentTestRuns] = useState<AsyncTaskExecutionPreview[]>([]);
  const [isLoadingRecentTestRuns, setIsLoadingRecentTestRuns] = useState<boolean>(false);
  const [isShowingRecentTestRunsModal, setIsShowingRecentTestRunsModal] = useState<boolean>(false);
  const [blueprintTestPayloads, setBlueprintTestPayloads] = useState<BlueprintTestPayload[]>([]);
  const [selectedTestPayload, setSelectedTestPayload] = useState<
    undefined | BlueprintTestPayload | null
  >();
  // Reducer for managing state of AbstractConditions for Selective Sync filter in Blueprint Editor
  const [
    abstractConditionsForSelectiveSync,
    dispatchAbstractConditionsForSelectiveSync,
  ] = useReducer<
    (
      state: AbstractConditionReducerState,
      action: AbstractConditionAction
    ) => AbstractConditionReducerState
  >(abstractConditionsForSelectiveSyncReducer, []);

  const { fetchTestLinkedAccounts } = useFetchTestLinkedAccounts({
    setIsLoadingTestLinkedAccounts,
    setTestLinkedAccounts,
    setSelectedTestLinkedAccount,
  });

  const updatedParameterSchema = blueprint.updated_parameter_schema_for_auto_update;

  const hasInputParameters =
    Object.keys(blueprint.parameter_schema?.properties ?? {}).length > 0 ||
    blueprint.scraper ||
    updatedParameterSchema;

  const fetchTestLinkedAccountsForBlueprint = useCallback<() => void>(() => {
    if (!integration.id) return;
    fetchTestLinkedAccounts(integration.id, category);
  }, [integration.id]);

  useEffect(() => {
    fetchTestLinkedAccountsForBlueprint();
  }, [integration.id, category]);

  useEffect(() => {
    if (!selectedTestLinkedAccount) return;
    fetchWithAuth({
      path: `/blueprints/${blueprint.id}/${selectedTestLinkedAccount}/test-model-ids`,
      method: "GET",
      onResponse: (results: { models?: CommonModelInfo[] }) => {
        if (!results.models) return;
        setTestCommonModels(results.models);
        if (results.models.length > 0) setSelectedTestCommonModel(results.models[0].id);
      },
      onError: () => {
        showErrorToast("Failed to fetch test common models.");
      },
    });
  }, [selectedTestLinkedAccount]);

  const { setBlueprintRunnerExecutionResponse } = useBlueprintContext();

  // VERY IMPORTANT: DO NOT EVER REMOVE
  useEffect(() => {
    setBlueprintRunnerExecutionResponse(null);
  }, [isCustomerLinkedAccountSelected]);

  // useEffect that checks if the selected linked account does not belong to the merge org, e.g. is a customer linked account
  // This is leveraged to disable input to ChatBPE if this is the case
  useEffect(() => {
    if (!selectedTestLinkedAccount) return;
    const selectedLinkedAccount = testLinkedAccounts.find(
      (linkedAccount) => linkedAccount.id === selectedTestLinkedAccount
    );
    if (selectedLinkedAccount?.organization?.is_merge_org === false) {
      setIsCustomerLinkedAccountSelected(true);
    } else {
      setIsCustomerLinkedAccountSelected(false);
    }
  }, [selectedTestLinkedAccount]);

  useEffect(() => {
    const hasInputParameters =
      blueprint.scraper ?? Object.keys(parameter_schema?.properties ?? {}).length > 0;
    if (blueprint.scraper) {
      const scraperParameterSchema: JSONObjectSchema = {
        type: "object",
        properties: {
          scraper_return_values: getReturnSchemaForScraper(blueprint.scraper),
        },
      };
      const initializedParams = initializeInputParams(
        scraperParameterSchema,
        shouldIncludeNestedParams,
        blueprint.operation_type
      );
      setInitializedSchemaForDeletePayload(JSON.stringify(initializedParams, null, 2));
      if (!selectedTestPayload) {
        setGlobalVarsAsString(JSON.stringify(initializedParams, null, 2));
      }
    } else if (parameter_schema && hasInputParameters) {
      const initializedParams = initializeInputParams(
        parameter_schema,
        shouldIncludeNestedParams,
        blueprint.operation_type
      );
      setInitializedSchemaForDeletePayload(JSON.stringify(initializedParams, null, 2));
      if (!selectedTestPayload) {
        setGlobalVarsAsString(JSON.stringify(initializedParams, null, 2));
      }
    }
  }, [parameter_schema, shouldIncludeNestedParams, blueprint?.scraper?.version_id]);

  useEffect(() => {
    if (blueprint.operation_type == BlueprintOperationType.PROXY) {
      const remoteId = testCommonModels?.find((i) => i.id === selectedTestCommonModel)?.remote_id;
      if (remoteId) {
        const initializedParams = initializeInputParamsForProxy(remoteId);
        setGlobalVarsAsString(JSON.stringify(initializedParams, null, 2));
        setInitializedSchemaForDeletePayload(JSON.stringify(initializedParams, null, 2));
      }
    }
  }, [selectedTestCommonModel]);

  const location = useLocation();
  const readmePathMatch = matchPath(location.pathname, BLUEPRINT_EDITOR_README_TAB_PATH);

  const isSelectedLinkedAccountASandboxAccount =
    testLinkedAccounts.find((linkedAccount) => linkedAccount.id === selectedTestLinkedAccount)
      ?.is_sandbox_account ?? false;

  const canAutogenerateMappingTestForBlueprint =
    [BlueprintVersionPublishState.Staged, BlueprintVersionPublishState.Published].includes(
      blueprint.version.publish_state
    ) &&
    !doesBlueprintHaveUnsavedChanges &&
    isSelectedLinkedAccountASandboxAccount &&
    isBlueprintWriteOperation(blueprint.operation_type);

  const integrationID = integration.id;
  const isQBDIntegration = integrationID === QUICKBOOKS_DESKTOP_INTEGRATION_ID;
  const tabs = readmePathMatch
    ? null
    : [
        {
          key: "overview",
          navbarText: "Overview",
          Icon: Box,
          content: <BlueprintEditorLeftPanelOverviewSubtab />,
        },
        {
          key: "console",
          navbarText: "Console",
          Icon: Sliders,
          content: (
            <BlueprintEditorLeftPanelConsoleSubtab
              integrationID={integrationID}
              isQBDIntegration={isQBDIntegration}
              hasInputParameters={hasInputParameters}
              updatedParameterSchema={updatedParameterSchema}
              blueprintTestPayloads={blueprintTestPayloads}
              shouldGenerateMappingTest={shouldGenerateMappingTest}
              setShouldGenerateMappingTest={setShouldGenerateMappingTest}
              setBlueprintTestPayloads={setBlueprintTestPayloads}
              selectedTestPayload={selectedTestPayload}
              setSelectedTestPayload={setSelectedTestPayload}
              selectedTestLinkedAccount={selectedTestLinkedAccount}
              setSelectedTestLinkedAccount={setSelectedTestLinkedAccount}
              selectedTestCommonModel={selectedTestCommonModel}
              setSelectedTestCommonModel={setSelectedTestCommonModel}
              isTestingBlueprint={isTestingBlueprint}
              setIsTestingBlueprint={setIsTestingBlueprint}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              fetchTestLinkedAccounts={fetchTestLinkedAccountsForBlueprint}
              isLoadingTestLinkedAccounts={isLoadingTestLinkedAccounts}
              testLinkedAccounts={testLinkedAccounts}
              testCommonModels={testCommonModels}
              disableFilterByDate={disableFilterByDate}
              setDisableFilterByDate={setDisableFilterByDate}
              disableSyncCursor={disableSyncCursor}
              canAutogenerateMappingTestForBlueprint={canAutogenerateMappingTestForBlueprint}
              setDisableSyncCursor={setDisableSyncCursor}
              shouldUseLastScraperResults={shouldUseLastScraperResults}
              setShouldUseLastScraperResults={setShouldUseLastScraperResults}
              maxLoopIterations={maxLoopIterations}
              setMaxLoopIterations={setMaxLoopIterations}
              maxLoopIterationsPerStep={maxLoopIterationsPerStep}
              setMaxLoopIterationsPerStep={setMaxLoopIterationsPerStep}
              maxPageIterations={maxPageIterations}
              setMaxPageIterations={setMaxPageIterations}
              frozenTimeValue={frozenTimeValue}
              setFrozenTimeValue={setFrozenTimeValue}
              overrideLastRunAtValue={overrideLastRunAtValue}
              setOverrideLastRunAtValue={setOverrideLastRunAtValue}
              shouldIncludeNestedParams={shouldIncludeNestedParams}
              setShouldIncludeNestedParams={setShouldIncludeNestedParams}
              globalVarsAsString={globalVarsAsString}
              setGlobalVarsAsString={setGlobalVarsAsString}
              initializedSchemaForDeletePayload={initializedSchemaForDeletePayload}
              isShowingModelsModal={isShowingModelsModal}
              setIsShowingModelsModal={setIsShowingModelsModal}
              shouldFilterDisabledFields={shouldFilterDisabledFields}
              setShouldFilterDisabledFields={setShouldFilterDisabledFields}
              shouldRunAsAsyncTask={shouldRunAsAsyncTask}
              setShouldRunAsAsyncTask={setShouldRunAsAsyncTask}
              asyncTaskExecutionID={asyncTaskExecutionID}
              setAsyncTaskExecutionID={setAsyncTaskExecutionID}
              asyncTaskExecutionResult={asyncTaskExecutionResult}
              setAsyncTaskExecutionResult={setAsyncTaskExecutionResult}
              recentTestRuns={recentTestRuns}
              setRecentTestRuns={setRecentTestRuns}
              isLoadingRecentTestRuns={isLoadingRecentTestRuns}
              setIsLoadingRecentTestRuns={setIsLoadingRecentTestRuns}
              isShowingRecentTestRunsModal={isShowingRecentTestRunsModal}
              setIsShowingRecentTestRunsModal={setIsShowingRecentTestRunsModal}
              doesBlueprintHaveUnsavedChanges={doesBlueprintHaveUnsavedChanges}
              abstractConditionsForSelectiveSync={abstractConditionsForSelectiveSync}
              dispatchAbstractConditionsForSelectiveSync={
                dispatchAbstractConditionsForSelectiveSync
              }
              codeBlockBranch={codeBlockBranch}
              setCodeBlockBranch={setCodeBlockBranch}
            />
          ),
        },
        {
          key: "schema",
          navbarText: "Schemas",
          Icon: List,
          content: <BlueprintEditorLeftPanelSchemasSubtab />,
        },
        ...(isQBDIntegration
          ? [
              {
                key: "web-connector",
                navbarText: "Connector",
                Icon: Settings,
                content: (
                  <BlueprintEditorLeftPanelConnectorSubtab
                    qbXMLQueryRequestFormat={qbxml_query_request_format}
                    parameterSchema={parameter_schema}
                    operationType={blueprint.operation_type}
                  />
                ),
              },
            ]
          : []),
      ];

  const versionID = version.id;

  const verticalTabs: IntegrationEditorLeftPanelVerticalTab[] = [
    {
      title: "Blueprint Editor",
      href: getBlueprintEditorPath(integrationID, versionID),
      Icon: Edit2,
    },
    {
      title: "Readme",
      href: getBlueprintReadmeTabPath(integrationID, versionID),
      Icon: FileText,
    },
    {
      title: "Field Validation",
      href: getBlueprintEditorValidationTabPath(integrationID, versionID),
      Icon: CheckSquare,
    },
    {
      title: "Parameters",
      href: getBlueprintEditorValidationTabPath(integrationID, versionID),
      Icon: Download,
    },
    {
      title: "Return values",
      href: getBlueprintEditorReturnSchemaTabPath(integrationID, versionID),
      Icon: Upload,
    },
  ].filter(
    (field) =>
      (field.title !== "Field Validation" ||
        OPERATION_TYPES_WITH_FIELD_VALIDATION.includes(operation_type)) &&
      (!["Parameters", "Return values"].includes(field.title) ||
        operation_type === BlueprintOperationType.FUNCTIONAL)
  );

  return (
    <div style={{ width: `${width}px` }} className="bg-white border-r border-[#dce4f0] shrink-0">
      <IntegrationEditorLeftPanel
        header={human_name || name}
        hasUnsavedChanges={doesBlueprintHaveUnsavedChanges}
        subheader={integration.name}
        integration={integration}
        tabs={tabs}
        verticalTabs={verticalTabs}
      />
    </div>
  );
};

export default BlueprintEditorLeftPanel;
